import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Upload, Loader2, Camera } from "lucide-react";
import CropModal from "@/components/CropModal";
import { isCroppableImage } from "@/lib/imageProcess";

const yen = (n) => (n == null ? "—" : "¥" + Number(n).toLocaleString("ja-JP"));

function ResultCard({ r }) {
  const conf = Math.round((r.confidence || 0) * 100);
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-5" data-testid="ai-result">
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {r.date !== undefined && <div><span className="text-stone-500">日付：</span><span className="text-stone-900">{r.date || "—"}</span></div>}
        {r.vendor !== undefined && <div><span className="text-stone-500">取引先：</span><span className="text-stone-900">{r.vendor || "—"}</span></div>}
        {r.amount !== undefined && <div><span className="text-stone-500">金額：</span><span className="text-stone-900 font-semibold">{yen(r.amount)}</span></div>}
        <div><span className="text-stone-500">税率：</span><span className="text-stone-900">{r.tax_rate != null ? r.tax_rate + "%" : "—"}</span></div>
        <div><span className="text-stone-500">勘定科目：</span><span className="text-emerald-900 font-semibold" data-testid="ai-account">{r.suggested_account || "—"}</span></div>
        <div><span className="text-stone-500">税区分：</span><span className="text-stone-900">{r.tax_category || "—"}</span></div>
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
          <span>AI信頼度</span><span data-testid="ai-confidence">{conf}%</span>
        </div>
        <div className="h-2 rounded-full bg-stone-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${conf}%` }} /></div>
      </div>
      {r.reason && <p className="mt-3 text-sm text-stone-600"><span className="font-medium text-stone-700">判断理由：</span>{r.reason}</p>}
      {r.similar_note && <p className="mt-1 text-xs text-stone-500">{r.similar_note}</p>}
    </div>
  );
}

export default function AIAssist() {
  const fileRef = useRef();
  const cameraRef = useRef();
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [cropSrc, setCropSrc] = useState(null);

  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [vendor, setVendor] = useState("");
  const [sugBusy, setSugBusy] = useState(false);
  const [sugResult, setSugResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isCroppableImage(file)) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
      return;
    }
    setFileName(file.name);
    doOcr(file, file.name || "receipt.pdf");
  };

  const doOcr = async (fileOrBlob, filename) => {
    setOcrBusy(true); setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append("file", fileOrBlob, filename);
      const { data } = await api.post("/ai/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrResult(data);
      toast.success("読み取りが完了しました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setOcrBusy(false); }
  };

  const handleOcrCropApply = async (blob) => {
    setCropSrc(null);
    await doOcr(blob, "receipt.jpg");
  };

  const cancelOcrCrop = () => {
    setCropSrc(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const suggest = async () => {
    if (!desc.trim()) { toast.error("取引内容を入力してください"); return; }
    setSugBusy(true); setSugResult(null);
    try {
      const { data } = await api.post("/ai/suggest-entry", { description: desc, amount: amount ? Number(amount) : null, vendor });
      setSugResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSugBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="ai-page">
      <CropModal src={cropSrc} onCancel={cancelOcrCrop} onApply={handleOcrCropApply} busy={ocrBusy} />
      <h1 className="font-display font-bold text-3xl text-stone-900 flex items-center gap-2">
        <Sparkles className="h-6 w-6 text-emerald-700" />AI仕訳アシスト
      </h1>
      <p className="mt-2 text-sm text-stone-500">領収書を読み取って仕訳を提案します。提案は確認・承認のうえでご利用ください（AIは提案のみ）。</p>

      <div className="mt-8 rounded-lg border border-stone-200 bg-white p-6">
        <div className="font-medium text-stone-900">領収書・請求書のOCR読み取り</div>
        <p className="mt-1 text-sm text-stone-500">画像またはPDFをアップロードすると、日付・取引先・金額・勘定科目・税区分を抽出します。</p>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="ai-ocr-input" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="ai-ocr-camera" />
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <button onClick={() => cameraRef.current?.click()} disabled={ocrBusy} data-testid="ai-ocr-camera-button"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
            {ocrBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {ocrBusy ? "読み取り中…" : "撮影"}
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={ocrBusy} data-testid="ai-ocr-button"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-60">
            <Upload className="h-4 w-4" />
            ファイルを選択
          </button>
          {fileName && <span className="ml-1 text-sm text-stone-500">{fileName}</span>}
        </div>
        {ocrResult && <ResultCard r={ocrResult} />}
      </div>

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6">
        <div className="font-medium text-stone-900">取引内容から仕訳を提案</div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><Label className="text-sm">摘要（取引内容）</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="ai-desc" className="mt-1.5" placeholder="例：タクシー代 打合せ移動" /></div>
          <div><Label className="text-sm">金額</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ai-amount" className="mt-1.5" placeholder="1200" /></div>
          <div className="sm:col-span-3"><Label className="text-sm">取引先（任意）</Label><Input value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="ai-vendor" className="mt-1.5" placeholder="日本交通" /></div>
        </div>
        <button onClick={suggest} disabled={sugBusy} data-testid="ai-suggest-button"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
          {sugBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          仕訳を提案
        </button>
        {sugResult && <ResultCard r={sugResult} />}
      </div>
    </div>
  );
}
